import React, { useState } from "react";
import { Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, IconButton, TextField } from "@mui/material";
import { Close as CloseIcon, EditOutlined } from "@mui/icons-material";
import toast from "react-hot-toast";
import axiosInstant from "../../api";
import { deleteProductPdpUrl, productPdpUrlVideo, updateProductPdpUrl } from "../../api/endpoints";
import { IconClose, IconGoToTab, IconLink, IconProduct, IconTrash, IconVideo } from "../../utils/icons";
import { linkForPageVideo, PageVideo, PdpUrlVideoLink } from "./PdpJsonLdOutput";
import { PdpChecks, PdpChecksStrip, PdpPixelPill, PdpTagPill } from "./PdpChecks";
import { ConfirmationDialog } from "../ConfirmationDialog/ConfirmationDialog";
import { SkuPdpLinkEditor } from "../SkuLinks/SkuPdpLinkEditor";
import { getVideoThumbnail } from "../../utils/downloadHelpers";
import { useUser } from "../../hooks/useUser";
import { isInternal } from "../../utils";
import { apiErrorCode } from "../../utils/apiError";
import { scoringLimitMessage } from "../../utils/plans";
import { PdpAutopilotDialog } from "./PdpAutopilotDialog";
import { BetaPill } from "../BetaPill";
import { pdpVideoHref } from "../../pages/admin/videos/selectedVideoCardV2/pdpAssignment";
import { formatVideoName } from "../../pages/admin/videos/hooks/useFetchVideos";

// One product page's summary, the same on Product settings > Product Pages
// and Video settings > Product pages: the retailer (and a paused page's
// Activate), the URL (edit: URL + SKUs) with the Autopilot (click: its
// settings — analysis interval, turn off), tag and Shopify pixel chips, the analysis checks, SKUs, the videos on the page (each removable)
// and Delete. Owns those actions and their dialogs; the caller reloads its
// rows through `onChanged` after any of them.

export interface PdpSummaryRow {
    id: number;
    pdp_url: string;
    retailer?: string | null;
    brand_id?: number | null;
    score_interval?: string | null;
    score_weekday?: number | null;
    score_day_of_month?: number | null;
    max_score_interval?: string | null;
    active?: boolean | null;
    autopilot?: boolean;
    videos?: PdpUrlVideoLink[] | null;
    page_videos?: PageVideo[] | null;
    skus?: string[];
    checks?: PdpChecks | null;
}

// A linked video the last PDP analysis did not see on the page: it looked
// (page_videos is a list, not null) and no page video was matched to this
// link. Null when the page was never analyzed or the video is on it.
const notOnPageNote = (row: PdpSummaryRow, link: PdpUrlVideoLink): string | null => {
    if (!Array.isArray(row.page_videos)) return null;
    const onPage = row.page_videos.some((v) => linkForPageVideo(row as any, v)?.s3_file_id === link.s3_file_id);
    if (onPage) return null;
    const others = row.page_videos.length;
    if (others === 0) return "Not found on this page by the last PDP analysis, which saw no video there.";
    return `Not found on this page by the last PDP analysis, which saw ${others === 1 ? "1 other video" : `${others} other videos`} there.`;
};

export function PdpPageSummary({
    row,
    desireid,
    onChanged,
    currentVideoId = null,
}: {
    row: PdpSummaryRow;
    desireid: number;
    // Reload after a change; `deleted` when the row itself is gone.
    onChanged: (opts: { deleted?: boolean }) => Promise<unknown> | void;
    // The video whose settings this is shown in (Video settings): its card is
    // marked "This video".
    currentVideoId?: number | null;
}) {
    const { role } = useUser();
    const [toggling, setToggling] = useState(false);
    const [autopilotOpen, setAutopilotOpen] = useState(false);
    const [confirmDelete, setConfirmDelete] = useState(false);
    const [deleting, setDeleting] = useState(false);
    const [linkToRemove, setLinkToRemove] = useState<PdpUrlVideoLink | null>(null);
    const [unlinking, setUnlinking] = useState(false);
    const [editing, setEditing] = useState(false);
    const [editUrl, setEditUrl] = useState("");
    const [editSkus, setEditSkus] = useState<string[]>([]);
    const [editError, setEditError] = useState<string | null>(null);
    const [savingEdit, setSavingEdit] = useState(false);
    const links = row.videos || [];

    // Activate a paused page's scoring (PATCH active). Can 409 when the
    // brand's plan is at its scoring limit.
    const handleActivate = async () => {
        setToggling(true);
        try {
            await axiosInstant.patch(updateProductPdpUrl(desireid, row.id), { active: true });
            toast.success("Scoring activated for this PDP URL.");
            await onChanged({});
        } catch (e: any) {
            toast.error(scoringLimitMessage(e) || "Couldn't update scoring for this PDP URL. Please try again.");
        } finally {
            setToggling(false);
        }
    };

    const handleDelete = async () => {
        setDeleting(true);
        try {
            await axiosInstant.delete(deleteProductPdpUrl(desireid, row.id));
            toast.success("Successfully deleted PDP URL and its SKUs!");
            setConfirmDelete(false);
            await onChanged({ deleted: true });
        } catch (e: any) {
            toast.error(e?.response?.data?.message || "Couldn't delete this PDP URL. Please try again.");
        } finally {
            setDeleting(false);
        }
    };

    // Take one of our videos off the page: its link (file URL, overrides,
    // VideoObject) goes; the page, its SKUs, score and other videos stay.
    const handleUnlink = async () => {
        if (!linkToRemove) return;
        setUnlinking(true);
        try {
            await axiosInstant.delete(productPdpUrlVideo(desireid, row.id, linkToRemove.s3_file_id));
            toast.success("Video removed from this page");
            setLinkToRemove(null);
            await onChanged({});
        } catch (e: any) {
            toast.error(e?.response?.data?.message || "Couldn't remove this video from the page. Please try again.");
        } finally {
            setUnlinking(false);
        }
    };

    const openEdit = () => {
        setEditUrl(row.pdp_url);
        setEditSkus(row.skus || []);
        setEditError(null);
        setEditing(true);
    };

    const handleSaveEdit = async () => {
        const trimmed = editUrl.trim();
        if (!trimmed || !/^https?:\/\//i.test(trimmed)) {
            setEditError("Enter a full URL starting with http:// or https://");
            return;
        }
        setSavingEdit(true);
        setEditError(null);
        try {
            await axiosInstant.patch(updateProductPdpUrl(desireid, row.id), { pdp_url: trimmed, skus: editSkus });
            toast.success("PDP URL updated.");
            setEditing(false);
            // A changed anchor regenerates JSON-LD server-side — reload.
            await onChanged({});
        } catch (e: any) {
            if (e?.response?.status === 409 || apiErrorCode(e) === "PdpUrlAlreadyExists") {
                setEditError("That URL is already on this product.");
            } else {
                setEditError("Couldn't save the PDP URL. Please try again.");
            }
        } finally {
            setSavingEdit(false);
        }
    };

    return (
        <>
            <Box className="flex items-start justify-between gap-3 flex-wrap mb-3">
                <Box className="flex flex-col gap-[6px] min-w-0 flex-1">
                    {(row.retailer || row.active === false) && (
                        <Box className="flex items-center gap-2 flex-wrap">
                            {row.retailer && (
                                <span className="text-[11px] font-bold text-text-2 bg-surface-2 rounded-[6px] px-2 py-[3px] uppercase tracking-[0.5px]">
                                    {row.retailer}
                                </span>
                            )}
                            {row.active === false && (
                                <>
                                    <span
                                        className="text-[11px] font-semibold text-down border border-down/40 rounded-[6px] px-2 py-[3px]"
                                        title="Not scored: the brand's plan is at its scoring limit or scoring was paused"
                                    >
                                        Scoring paused
                                    </span>
                                    <button
                                        type="button"
                                        onClick={handleActivate}
                                        disabled={toggling}
                                        className="text-[11px] font-semibold text-text-2 hover:text-text-1 underline underline-offset-2 disabled:opacity-60"
                                        title="Count this product against the plan's scoring limit and start scoring it"
                                    >
                                        {toggling ? "…" : "Activate scoring"}
                                    </button>
                                </>
                            )}
                        </Box>
                    )}
                    <Box className="flex items-center gap-[4px] min-w-0 flex-wrap">
                        {/* Left of the URL, as on the Autopilot rows. */}
                        {row.autopilot && isInternal(role) && (
                            <button
                                type="button"
                                onClick={() => setAutopilotOpen(true)}
                                className="inline-flex items-center gap-[5px] text-[11px] font-semibold border border-transparent rounded-[6px] px-2 py-[2px] bg-tint text-accent-text shrink-0 mr-1 hover:opacity-90 cursor-pointer"
                                title="On autopilot — click for its analysis interval, or to turn it off"
                            >
                                Autopilot on <BetaPill solid />
                            </button>
                        )}
                        <a
                            href={row.pdp_url}
                            target="_blank"
                            rel="noreferrer"
                            className="flex items-center gap-[6px] text-[13.5px] text-text-1 hover:text-accent-text min-w-0"
                        >
                            <IconLink size={14} stroke="var(--muted)" />
                            <span className="truncate max-w-[420px]">{row.pdp_url}</span>
                        </a>
                        <button
                            type="button"
                            title="Edit PDP URL & SKUs"
                            aria-label="Edit PDP URL & SKUs"
                            onClick={openEdit}
                            className="w-6 h-6 flex items-center justify-center rounded text-muted hover:bg-surface-2 hover:text-text-2 shrink-0"
                        >
                            <EditOutlined sx={{ fontSize: 15 }} />
                        </button>
                        {/* What is installed on the page's site: the script
                            tag and the Shopify pixel (as on the Autopilot rows). */}
                        <PdpTagPill checks={row.checks} className="ml-1" />
                        <PdpPixelPill checks={row.checks} />
                    </Box>
                    {/* The basic checks, in the order things land on a page:
                        our embed, the videos it shows, the markup for them. */}
                    <Box className="flex items-center gap-[8px] flex-wrap mt-[2px]">
                        <PdpChecksStrip checks={row.checks} />
                    </Box>
                    {(row.skus || []).length > 0 && (
                        <Box className="flex items-center gap-[6px] flex-wrap min-w-0">
                            <IconProduct width={14} height={14} stroke="var(--muted)" />
                            {(row.skus || []).map((sku) => (
                                <span
                                    key={sku}
                                    className="px-[7px] py-[2px] rounded-[6px] bg-surface-2 border border-border-token font-mono text-[11.5px] text-text-2"
                                >
                                    {sku}
                                </span>
                            ))}
                        </Box>
                    )}
                    {/* One card per video on the page, each with the file URL the
                        retailer serves for it. */}
                    {links.map((link) => {
                        const href = pdpVideoHref(isInternal(role), link.s3_file_id);
                        const name = formatVideoName(link.videoFile?.name || link.videoFile?.key);
                        const isCurrent = currentVideoId != null && Number(link.s3_file_id) === Number(currentVideoId);
                        return (
                            <Box key={link.id} className="flex flex-col gap-[4px] mt-1">
                                <Box className="flex items-center gap-[8px] flex-wrap">
                                    <a
                                        href={href || undefined}
                                        target="_blank"
                                        rel="noreferrer"
                                        title={name || "View video"}
                                        className="group inline-flex items-center gap-[10px] self-start p-[6px] pr-[12px] rounded-[10px] bg-surface border border-border-token hover:border-accent transition-colors max-w-[420px]"
                                    >
                                        <span className="relative shrink-0">
                                            <img
                                                src={getVideoThumbnail(link.videoFile || {})}
                                                alt=""
                                                className="h-[40px] w-[68px] rounded-[6px] object-cover bg-surface-2"
                                            />
                                            <span className="absolute inset-0 flex items-center justify-center">
                                                <span className="flex items-center justify-center h-[18px] w-[18px] rounded-full bg-black/55">
                                                    <span className="ml-[2px] border-y-[4px] border-y-transparent border-l-[6px] border-l-white" />
                                                </span>
                                            </span>
                                        </span>
                                        <span className="flex flex-col min-w-0 gap-[1px]">
                                            <span className="truncate text-[12.5px] font-semibold text-text-1">
                                                {name || "Linked video"}
                                            </span>
                                            <span className="inline-flex items-center gap-[4px] text-[11px] text-muted group-hover:text-accent-text transition-colors">
                                                <IconGoToTab width={11} height={11} stroke="currentColor" />
                                                View video
                                            </span>
                                        </span>
                                    </a>
                                    {isCurrent && (
                                        <span className="text-[11px] font-semibold rounded-[6px] px-2 py-[2px] bg-tint text-accent-text">
                                            This video
                                        </span>
                                    )}
                                    <button
                                        type="button"
                                        onClick={() => setLinkToRemove(link)}
                                        disabled={unlinking}
                                        title="Remove this video from the page — the page, its SKUs, its score and any other videos on it stay"
                                        className="inline-flex items-center gap-[4px] px-[9px] py-[5px] rounded-[7px] text-[11.5px] font-semibold text-text-2 bg-surface border border-border-token hover:border-[#F0C9C4] hover:text-[var(--down)] transition-colors disabled:opacity-60"
                                    >
                                        <IconClose size={10} stroke="currentColor" />
                                        Remove from page
                                    </button>
                                </Box>
                                {link.video_file_url && (
                                    <span className="flex items-center gap-[6px] text-[12.5px] text-muted min-w-0">
                                        <IconVideo size={14} stroke="var(--muted)" />
                                        <span className="truncate max-w-[420px]">{link.video_file_url}</span>
                                    </span>
                                )}
                                {notOnPageNote(row, link) && (
                                    <span className="text-[11.5px] text-[var(--down)]">{notOnPageNote(row, link)}</span>
                                )}
                            </Box>
                        );
                    })}
                    {links.length === 0 && (
                        <span className="text-[12px] text-muted mt-1">
                            None of our videos is on this page yet — add one from the video&apos;s settings.
                        </span>
                    )}
                </Box>
                <button
                    type="button"
                    onClick={() => setConfirmDelete(true)}
                    title="Delete PDP URL & SKUs"
                    className="shrink-0 p-2 rounded-lg border border-border-token bg-surface hover:border-[#F0C9C4] transition-colors"
                >
                    <IconTrash width={15} height={15} stroke="var(--down)" />
                    <span className="sr-only">Delete PDP URL & SKUs</span>
                </button>
            </Box>

            {autopilotOpen && (
                <PdpAutopilotDialog
                    open={autopilotOpen}
                    onClose={() => setAutopilotOpen(false)}
                    desireid={desireid}
                    brandId={row.brand_id || null}
                    page={row}
                    onChanged={() => onChanged({})}
                />
            )}

            <Dialog open={editing} onClose={savingEdit ? undefined : () => setEditing(false)} maxWidth="sm" fullWidth>
                <DialogTitle sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                    Edit PDP URL & SKUs
                    <IconButton aria-label="Close" onClick={() => setEditing(false)} size="small" disabled={savingEdit}>
                        <CloseIcon fontSize="small" />
                    </IconButton>
                </DialogTitle>
                <DialogContent>
                    <TextField
                        autoFocus
                        fullWidth
                        size="small"
                        label="PDP URL"
                        placeholder="https://retailer.com/p/product…"
                        value={editUrl}
                        onChange={(e) => setEditUrl(e.target.value)}
                        error={!!editError}
                        helperText={editError || "Changing the URL regenerates the JSON-LD for this page."}
                        sx={{ mt: 1, mb: 2 }}
                    />
                    {/* SKUs save on the same PATCH as the URL — editing them
                        never touches the row's scores or generated JSON-LD. */}
                    <SkuPdpLinkEditor value={editSkus} onChange={setEditSkus} disabled={savingEdit} />
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2 }}>
                    <Button onClick={() => setEditing(false)} disabled={savingEdit}>
                        Cancel
                    </Button>
                    <Button variant="contained" disableElevation onClick={handleSaveEdit} disabled={savingEdit}>
                        {savingEdit ? "Saving…" : "Save"}
                    </Button>
                </DialogActions>
            </Dialog>

            <ConfirmationDialog
                open={confirmDelete}
                onClose={() => setConfirmDelete(false)}
                onConfirm={handleDelete}
                title="Delete PDP URL & SKUs?"
                description="This permanently removes the page, its SKUs, its scoring history and any generated JSON-LD. This action cannot be undone."
                confirmLabel={deleting ? "Deleting…" : "Delete"}
                cancelLabel="Cancel"
                maxWidth="xs"
            />
            <ConfirmationDialog
                open={!!linkToRemove}
                onClose={() => setLinkToRemove(null)}
                onConfirm={handleUnlink}
                title="Remove this video from the page?"
                description={`${formatVideoName(linkToRemove?.videoFile?.name || linkToRemove?.videoFile?.key) || "This video"} will no longer be associated with this page, and its Video Object JSON-LD for the page is discarded. The page, its SKUs, its score and any other videos on it stay. You can add it back from the video's settings.`}
                confirmLabel={unlinking ? "Removing…" : "Remove"}
                cancelLabel="Cancel"
                maxWidth="xs"
            />
        </>
    );
}
